import { useId, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { sfx } from "../../lib/audio";

export const V = 2048;
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const lenOf = (t) => t.reduce((s, p, i) => (i ? s + dist(p, t[i - 1]) : 0), 0);
const line = (t) => (t.length > 1 ? t : [t[0], [t[0][0] + 1, t[0][1]]]).map((p) => `${p[0]},${p[1]}`).join(" ");
const width = (t) => Math.max(...t.map((p) => p[2] || 150)) * 2.4;

export const Grid = () => (
  <g stroke="#E7D7C1" strokeWidth="10" strokeDasharray="40 40" fill="none">
    <rect x="20" y="20" width={V - 40} height={V - 40} rx="80" strokeDasharray="none" />
    <line x1={V / 2} y1="20" x2={V / 2} y2={V - 20} /><line x1="20" y1={V / 2} x2={V - 20} y2={V / 2} />
  </g>
);

// Animated stroke drawing: each median track is revealed inside its outline.
const Draw = ({ strokes, id, color, speed = 1, only }) => {
  let t = 0;
  return strokes.map((s, i) => {
    const d = Math.max(0.4, lenOf(s.track) / 1700) / speed;
    const delay = only === undefined ? t : 0;
    t += d + 0.3 / speed;
    if (only !== undefined && only !== i) return null;
    const L = lenOf(s.track) + 20;
    return (
      <polyline key={i} points={line(s.track)} fill="none" stroke={color} strokeWidth={width(s.track)} strokeLinecap="round" strokeLinejoin="round"
        clipPath={`url(#${id}c${i})`} style={{ opacity: 0, strokeDasharray: `${L} ${L + 4000}`, strokeDashoffset: L, animation: `zy-draw ${d}s linear ${delay}s forwards` }} />
    );
  });
};

const Clips = ({ strokes, id }) => <defs>{strokes.map((s, i) => <clipPath id={`${id}c${i}`} key={i}><path d={s.outline} /></clipPath>)}</defs>;

export const StrokeAnim = ({ strokes, playKey = 0, color = "#C83E2B", className = "", testid = "stroke-anim" }) => {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox={`0 0 ${V} ${V}`} className={className} data-testid={testid}>
      <Clips strokes={strokes} id={id} />
      <Grid />
      {strokes.map((s, i) => <path key={i} d={s.outline} fill="#EFE3CF" />)}
      <g key={playKey}><Draw strokes={strokes} id={id} color={color} /></g>
      {strokes.map((s, i) => (
        <text key={i} x={s.track[0][0] + (strokes.slice(0, i).filter((o) => dist(o.track[0], s.track[0]) < 260).length * 150)} y={Math.max(s.track[0][1], 240)} dy="-90" textAnchor="middle" fontSize="130" fontWeight="900" fill="#78350F">{i + 1}</text>
      ))}
    </svg>
  );
};

// Glyph drawn over a card picture, like the tracing line on paper flashcards.
export const GlyphOverlay = ({ strokes, className = "" }) => (
  <svg viewBox={`0 0 ${V} ${V}`} className={className} aria-hidden>
    {strokes.map((s, i) => <path key={i} d={s.outline} fill="rgba(255,255,255,0.72)" stroke="#5b8fb0" strokeWidth="14" />)}
    {strokes.map((s, i) => <circle key={`d${i}`} cx={s.track[0][0]} cy={s.track[0][1]} r="44" fill="#334155" />)}
  </svg>
);

const TOL = 360;
const traced = (ink, tr) => {
  if (!ink.length) return false;
  if (tr.length === 1) return dist(ink[0], tr[0]) < TOL;
  if (ink.length < 3 || dist(ink[0], tr[0]) > TOL * 1.3 || dist(ink[ink.length - 1], tr[tr.length - 1]) > TOL * 1.3) return false;
  const idx = tr.map((p) => {
    let best = 0;
    ink.forEach((q, j) => { if (dist(p, q) < dist(p, ink[best])) best = j; });
    return dist(p, ink[best]) < TOL ? best : -1;
  });
  return idx.every((v, i) => v >= 0 && (i === 0 || v >= idx[i - 1] - 2));
};

export const StrokeTrace = ({ strokes, onDone, testid = "stroke-trace" }) => {
  const id = useId().replace(/:/g, "");
  const svg = useRef(null);
  const drawing = useRef(false);
  const mistakes = useRef(0);
  const [cur, setCur] = useState(0);
  const [ink, setInk] = useState([]);
  const [hint, setHint] = useState(0);
  const [shake, setShake] = useState(0);
  const reset = () => { setCur(0); setInk([]); setHint(0); mistakes.current = 0; };
  const done = cur >= strokes.length;

  const pt = (e) => {
    const r = svg.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * V, ((e.clientY - r.top) / r.height) * V];
  };
  const down = (e) => {
    if (done) return;
    svg.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    setInk([pt(e)]);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pt(e);
    setInk((k) => (dist(k[k.length - 1], p) > 18 ? [...k, p] : k));
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (traced(ink, strokes[cur].track)) {
      sfx("good");
      const next = cur + 1;
      setCur(next);
      setHint(0);
      if (next >= strokes.length) onDone?.({ mistakes: mistakes.current });
    } else {
      sfx("bad");
      mistakes.current += 1;
      setShake((s) => s + 1);
      setHint((h) => h + 1);
    }
    setInk([]);
  };

  const s = strokes[cur];
  return (
    <div className="flex flex-col items-center gap-3" data-testid={testid}>
      <div key={shake} className={shake ? "shake" : ""}>
        <svg ref={svg} viewBox={`0 0 ${V} ${V}`} className="w-[min(78vw,380px)] h-[min(78vw,380px)] bg-[#FFFDF7] rounded-[2rem] border-4 border-amber-200 shadow-inner touch-none select-none"
          onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} data-testid={`${testid}-pad`}>
          <Clips strokes={strokes} id={id} />
          <Grid />
          {strokes.map((x, i) => <path key={i} d={x.outline} fill={i < cur ? "#2E7D32" : i === cur ? "#FDE3B5" : "#EFE3CF"} />)}
          {s && hint > 0 && <g key={hint}><Draw strokes={strokes} id={id} color="#F59E0B" only={cur} /></g>}
          {s && <polyline points={line(s.track)} fill="none" stroke="#fff" strokeWidth="26" strokeDasharray="10 60" strokeLinecap="round" />}
          {s && <><circle cx={s.track[0][0]} cy={s.track[0][1]} r="100" fill="#2E7D32" /><text x={s.track[0][0]} y={s.track[0][1]} dy="46" textAnchor="middle" fontSize="130" fontWeight="900" fill="#fff">{cur + 1}</text></>}
          {ink.length > 0 && <polyline points={line(ink)} fill="none" stroke="#1E3A8A" strokeWidth="110" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />}
        </svg>
      </div>
      <div className="flex items-center gap-3 font-round text-xl text-stone-600">
        <span data-testid={`${testid}-status`}>{done ? "寫好了！" : `第 ${cur + 1} 畫 / 共 ${strokes.length} 畫`}</span>
        <button data-testid={`${testid}-reset`} onClick={reset} className="w-12 h-12 rounded-full bg-white border-2 border-amber-200 text-[var(--tea)] flex items-center justify-center"><RotateCcw size={20} /></button>
      </div>
    </div>
  );
};
